import { useState } from "react";
import type { RelatedPlace } from "~/lib/api";
import { toast } from "~/components/ui/toaster";
import { groupRelatedPlaces, type RelatedPlaceCategory } from "~/lib/relatedPlaces";
import { useEditorMode } from "~/lib/editorModeStore";
import { PanelSection } from "~/components/ui/panel-section";
import { PlaceCategory } from "./PlaceCategory";

interface RelatedPlacesSectionProps {
  title: string;
  placeId: string;
  relatedPlaces: RelatedPlace[];
  categories: RelatedPlaceCategory[];
  canEdit?: boolean;
  onRemove?: (relatedPlaceId: string) => Promise<void>;
  onSelect?: (place: RelatedPlace) => void;
}

export function RelatedPlacesSection({
  title,
  placeId,
  relatedPlaces,
  categories,
  canEdit,
  onRemove,
  onSelect,
}: RelatedPlacesSectionProps) {
  const startLinking = useEditorMode((s) => s.startLinking);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const grouped = groupRelatedPlaces(relatedPlaces, categories);

  const visibleCategories = canEdit
    ? categories
    : categories.filter((cat) => (grouped.get(cat.label)?.length ?? 0) > 0);

  if (visibleCategories.length === 0) return null;

  function handleAdd(category: RelatedPlaceCategory) {
    startLinking({
      sourcePlaceId: placeId,
      allowedTypes: category.types,
      categoryLabel: category.label,
    });
  }

  async function handleRemove(relatedPlaceId: string) {
    if (!onRemove) return;
    setRemovingId(relatedPlaceId);
    try {
      await onRemove(relatedPlaceId);
    } catch {
      toast.error("Failed to remove place");
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <>
      <PanelSection.Separator />
      <section aria-label={title}>
        <PanelSection.Root>
          <PanelSection.Title>{title}</PanelSection.Title>
          <PanelSection.Content className="space-y-4">
            {visibleCategories.map((category) => (
              <PlaceCategory
                key={category.label}
                category={category}
                places={grouped.get(category.label) ?? []}
                canEdit={canEdit}
                onAdd={() => handleAdd(category)}
                onRemove={handleRemove}
                onSelect={onSelect}
                removingId={removingId}
              />
            ))}
          </PanelSection.Content>
        </PanelSection.Root>
      </section>
    </>
  );
}
